// Native permission prompts bridge.
//
// Web: most permissions can't be queried meaningfully — we return "prompt" or "unsupported".
// Native (iOS): wraps Capacitor plugin permission APIs. Some prompts (photo library Add-Only
// vs Read/Write, notifications) need explicit Info.plist usage descriptions.
//
// TODO(ios-native): For App Store submission, ensure ios/App/App/Info.plist includes:
//   - NSLocationWhenInUseUsageDescription  ("Used to record your trip route while the app is open.")
//   - NSLocationAlwaysAndWhenInUseUsageDescription
//                                          ("Continues recording your route in the background so we
//                                            don't miss the drive between camps.")
//   - NSPhotoLibraryUsageDescription       ("Reads photos taken during your trip dates so they sync
//                                            to the group library.")
//   - NSCameraUsageDescription             ("Only used if you tap 'Take photo' to add a memory.")
//   - NSUserNotificationsUsageDescription is NOT required, but you must call
//     UNUserNotificationCenter.current().requestAuthorization(...) — wire this through a tiny
//     custom Capacitor plugin or use @capacitor/push-notifications.

import { Geolocation } from "@capacitor/geolocation";
import { Camera } from "@capacitor/camera";
import { LocalNotifications } from "@capacitor/local-notifications";
import { isNative } from "./platform";
import { LocationBG } from "./location-bg";

export type PermissionState = "granted" | "denied" | "prompt" | "limited" | "unsupported";

export async function checkLocationPermission(): Promise<PermissionState> {
  if (!isNative()) return "unsupported";
  const res = await Geolocation.checkPermissions();
  return (res.location as PermissionState) ?? "prompt";
}

export async function requestLocationAlways(): Promise<PermissionState> {
  if (!isNative()) return "unsupported";
  // First grant "When In Use" via the standard plugin (iOS requires this before it will
  // show the Always upgrade prompt at all).
  await Geolocation.requestPermissions({ permissions: ["location"] });
  // Then ask the custom LocationBG plugin to call requestAlwaysAuthorization().
  // Falls back to whatever WhenInUse status if the plugin isn't compiled into this build yet.
  try {
    const res = await LocationBG.requestAlways();
    if (res.status === "always") return "granted";
    if (res.status === "whenInUse") return "granted"; // partial — caller can re-prompt later
    if (res.status === "denied" || res.status === "restricted") return "denied";
    return "prompt";
  } catch {
    const res = await Geolocation.checkPermissions();
    return (res.location as PermissionState) ?? "prompt";
  }
}

export async function checkPhotoLibraryPermission(): Promise<PermissionState> {
  if (!isNative()) return "unsupported";
  const res = await Camera.checkPermissions();
  return (res.photos as PermissionState) ?? "prompt";
}

export async function requestPhotoLibraryReadWrite(): Promise<PermissionState> {
  if (!isNative()) return "unsupported";
  // TODO(ios-native): Camera plugin's "photos" permission maps to PHPhotoLibrary
  // .requestAuthorization(for: .readWrite). "Limited" is a valid grant on iOS 14+ —
  // surface it in the UI so the user knows only a subset of photos can be scanned.
  const res = await Camera.requestPermissions({ permissions: ["photos"] });
  return (res.photos as PermissionState) ?? "prompt";
}

export async function requestNotificationsPermission(): Promise<PermissionState> {
  if (!isNative()) return "unsupported";
  // iOS shows the system prompt exactly once per install — call sites should explain the value
  // (e.g. "we'll ping you when uploads are waiting for Wi-Fi") before invoking this.
  const res = await LocalNotifications.requestPermissions();
  if (res.display === "granted") return "granted";
  if (res.display === "denied") return "denied";
  return "prompt";
}
